import React, { useState, useMemo } from 'react';
import { Icon } from './icons';
import { BookCover, BookListRow, ProChip } from './common';
import { BookV2, ShelfV2 } from '../types';

type Dim = 'status' | 'genre' | 'shelf';

export const LibraryScreen: React.FC<{
  books: BookV2[];
  isPro: boolean;
  maxLibrary: number;
  maxReading?: number;
  onOpenBook: (b: BookV2) => void;
  onAdd: () => void;
  onOpenPaywall: () => void;
  // FP3 — estantes personalizadas
  shelves?: ShelfV2[];
  // A edição de estante mudou de casa (Biblioteca do perfil). Aqui fica só o
  // caminho para lá.
  onIrParaPerfil?: () => void;
  onBookActions?: (b: BookV2) => void;
  onBack?: () => void;
}> = ({ books, isPro, maxLibrary, maxReading = 2, onOpenBook, onAdd, onOpenPaywall, shelves = [], onIrParaPerfil, onBookActions, onBack }) => {
  const ALL = ' all', NONE = ' none'; // sentinelas que não colidem com gênero de texto livre
  const [dim, setDim] = useState<Dim>('status');
  const [sel, setSel] = useState<string>('reading');
  const [search, setSearch] = useState('');
  const norm = (s: string) => s.toLowerCase();

  const changeDim = (d: Dim) => { setDim(d); setSel(d === 'status' ? 'reading' : ALL); };

  const base = books.filter(b => !search || norm(b.title).includes(norm(search)) || norm(b.author).includes(norm(search)));

  const genres = useMemo(() => Array.from(new Set(books.map(b => b.genre).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b)), [books]);
  const hasNoGenre = books.some(b => !b.genre);

  // Chips por dimensão — mesma UI dos chips de status, só muda o conteúdo
  // Contagens dos chips a partir de `base` (pós-busca) — badge sempre concorda com a lista
  let chips: { id: string; label: string; count: number }[] = [];
  if (dim === 'status') {
    chips = [
      { id: 'reading', label: 'Lendo', count: base.filter(b => b.status === 'reading').length },
      { id: 'wishlist', label: 'Desejo', count: base.filter(b => b.status === 'wishlist').length },
      ...(books.filter(b => b.status === 'paused').length > 0 ? [{ id: 'paused', label: 'Parados', count: base.filter(b => b.status === 'paused').length }] : []),
      { id: 'finished', label: 'Lidos', count: base.filter(b => b.status === 'finished').length },
      { id: 'all', label: 'Todos', count: base.length },
    ];
  } else if (dim === 'genre') {
    chips = [
      { id: ALL, label: 'Todos', count: base.length },
      ...genres.map(g => ({ id: g, label: g, count: base.filter(b => b.genre === g).length })),
      ...(hasNoGenre ? [{ id: NONE, label: 'Sem gênero', count: base.filter(b => !b.genre).length }] : []),
    ];
  } else {
    chips = [
      { id: ALL, label: 'Todas', count: base.length },
      ...shelves.map(s => ({ id: s.id, label: (s.emoji ? s.emoji + ' ' : '') + s.name, count: base.filter(b => (b.shelfIds || []).includes(s.id)).length })),
    ];
  }

  const filtered = base.filter(b => {
    if (dim === 'status') return sel === 'all' || b.status === sel;
    if (dim === 'genre') return sel === ALL || (sel === NONE ? !b.genre : b.genre === sel);
    return sel === ALL || (b.shelfIds || []).includes(sel);
  });

  const atLimit = !isPro && books.length >= maxLibrary;
  const readingNow = books.filter(b => b.status === 'reading').length;
  const atReadingLimit = !isPro && readingNow >= maxReading;


  const chip = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', borderRadius: 999,
    background: active ? 'var(--ink)' : 'var(--paper-2)',
    color: active ? 'var(--paper)' : 'var(--ink)',
    border: active ? '1px solid var(--ink)' : '1px solid var(--line)',
    fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  });

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      <div style={{ padding: '40px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {onBack && (
              <button onClick={onBack} aria-label="Voltar" style={{
                width: 38, height: 38, borderRadius: '50%', background: 'var(--paper-2)', border: '1px solid var(--line)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
              }}><Icon name="chevron-left" size={18}/></button>
            )}
            <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Biblioteca</div>
          </div>
          <button onClick={atLimit ? onOpenPaywall : onAdd} style={{
            width: 38, height: 38, borderRadius: '50%', background: 'var(--ink)', color: 'var(--paper)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={atLimit ? 'lock' : 'plus'} size={18} color="var(--paper)" stroke={2} />
          </button>
        </div>
        <div className="serif" style={{ fontSize: 38, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em' }}>
          Minha estante<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>de livros.</span>
        </div>

        {!isPro && (
          <div style={{
            marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            background: atReadingLimit ? 'rgba(199,83,26,0.08)' : 'var(--paper-2)',
            border: `1px solid ${atReadingLimit ? 'rgba(199,83,26,0.3)' : 'var(--line)'}`, borderRadius: 12,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                <strong style={{ color: 'var(--ink)' }}>{readingNow}/{maxReading}</strong> lendo ao mesmo tempo
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                {atReadingLimit
                  ? `Você já lê ${maxReading} de cada vez no Free. Pause ou termine um pra começar outro — a estante é ilimitada.`
                  : 'Biblioteca ilimitada no Free — o limite é só de leituras ao mesmo tempo.'}
              </div>
            </div>
            <ProChip onClick={onOpenPaywall}/>
          </div>
        )}

        <div style={{
          marginTop: 20, background: 'var(--paper-2)', borderRadius: 14, padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--line)',
        }}>
          <Icon name="search" size={18} color="var(--ink-3)" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar na sua estante..." style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 14, color: 'var(--ink)' }}/>
        </div>
      </div>

      {/* FP3 — seletor de dimensão: separa por Status / Gênero / Estante */}
      <div style={{ marginTop: 18, padding: '0 20px' }}>
        <div style={{ display: 'inline-flex', gap: 4, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 12, padding: 4 }}>
          {([['status', 'Status'], ['genre', 'Gênero'], ['shelf', 'Estante']] as const).map(([d, label]) => (
            <button key={d} onClick={() => changeDim(d)} style={{
              padding: '7px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
              background: dim === d ? 'var(--ink)' : 'transparent', color: dim === d ? 'var(--paper)' : 'var(--ink-3)',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* chips (mesmo visual de sempre) */}
      <div style={{ marginTop: 12, padding: '0 20px', display: 'flex', gap: 6, overflowX: 'auto' }}>
        {chips.map(t => (
          <button key={t.id} onClick={() => setSel(t.id)} style={chip(sel === t.id)}>
            {t.label}
            <span style={{
              fontSize: 10, padding: '1px 6px', borderRadius: 999,
              background: sel === t.id ? 'rgba(255,255,255,0.18)' : 'var(--paper-3)',
              color: sel === t.id ? 'var(--paper)' : 'var(--ink-3)',
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Criar, renomear, reordenar e excluir estante saíram daqui: ficavam a
          três toques de distância do único lugar onde as estantes aparecem de
          verdade. Agora tudo isso mora na Biblioteca do perfil, junto com os
          livros. Aqui sobra o que esta tela faz bem — filtrar. */}
      {dim === 'shelf' && onIrParaPerfil && (
        <div style={{ padding: '12px 20px 0' }}>
          <button onClick={onIrParaPerfil} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'left', lineHeight: 1.5,
          }}>
            Criar e organizar estantes é no seu perfil, na aba Biblioteca —{' '}
            <span style={{ color: 'var(--ember)' }}>ir para lá</span>.
          </button>
        </div>
      )}

      {/* prateleira aberta (só em Status · Lendo) */}
      {dim === 'status' && sel === 'reading' && filtered.length > 0 && (
        <div style={{ marginTop: 22, padding: '0 20px' }}>
          <Shelf books={filtered.slice(0, 3)} total={filtered.length} onOpenBook={onOpenBook}/>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {filtered.length === 0 ? (
          dim === 'status' ? (
            <EmptyState onAdd={atLimit ? onOpenPaywall : onAdd} filter={sel}/>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 24, lineHeight: 1.15, color: 'var(--ink)' }}>{dim === 'shelf' ? 'Estante vazia' : 'Nada nesse gênero'}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, fontStyle: 'italic' }}>
                {dim === 'shelf' ? 'Adicione livros pelo menu “⋯” de cada livro.' : 'Defina o gênero dos seus livros no menu “⋯”.'}
              </div>
            </div>
          )
        ) : (
          filtered.map(b => <BookListRow key={b.id} book={b} onClick={() => onOpenBook(b)} onActions={onBookActions ? () => onBookActions(b) : undefined}/>)
        )}
        {atLimit && filtered.length > 0 && (
          <div style={{ padding: '24px 20px 0' }}>
            <button onClick={onOpenPaywall} style={{
              width: '100%', padding: '16px 20px', background: 'var(--ink)', color: 'var(--paper)', borderRadius: 16,
              display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="lock" size={16} color="var(--paper)"/></div>
              <div style={{ flex: 1 }}>
                <div className="serif" style={{ fontSize: 17, lineHeight: 1.2 }}>Estante ilimitada</div>
                <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>Destrave com o Leitura Pro</div>
              </div>
              <Icon name="chevron-right" size={18} color="var(--paper)"/>
            </button>
          </div>
        )}
      </div>

      {/* modal criar / renomear estante */}
    </div>
  );
};

// `total` é o do filtro: a vitrine só mostra 3 capas, e a frase contava as
// capas ("3 livros abertos" com "Lendo 4") — pente fino, QA-29.
function Shelf({ books, total, onOpenBook }: { books: BookV2[]; total?: number; onOpenBook: (b: BookV2) => void }) {
  const n = total ?? books.length;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', gap: 14, padding: '30px 20px 10px',
        background: 'var(--paper-2)', borderRadius: 22, border: '1px solid var(--line)',
        justifyContent: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 26, height: 2, background: 'var(--line-strong)', borderRadius: 1 }} />
        <div style={{ position: 'absolute', left: 20, right: 20, bottom: 14, height: 14, background: 'linear-gradient(to bottom, var(--paper-3), var(--paper-2))', borderRadius: 3 }} />
        {books.map((b, i) => (
          <button key={b.id} onClick={() => onOpenBook(b)} style={{
            background: 'none', transform: `rotate(${(i - 1) * 1.5}deg)`, transformOrigin: 'bottom center', position: 'relative', zIndex: 2,
          }}>
            <BookCover book={b} width={70} height={108} />
          </button>
        ))}
      </div>
      <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
        {n} {n === 1 ? 'livro aberto' : 'livros abertos'}
      </div>
    </div>
  );
}

function EmptyState({ onAdd, filter }: { onAdd: () => void; filter: string }) {
  const text: Record<string, { t: string; s: string; b: string }> = {
    reading:  { t: 'Sem livros abertos', s: 'O hábito começa com o primeiro.', b: 'Adicionar livro' },
    wishlist: { t: 'Sem desejos na lista', s: 'Próximas leituras ficam aqui.', b: 'Salvar um livro' },
    paused:   { t: 'Nenhum livro parado', s: 'Livros que você pausou ficam aqui — é só retomar quando quiser.', b: 'Adicionar livro' },
    finished: { t: 'Ainda sem terminados', s: 'Toda primeira vez foi aqui.', b: 'Começar a ler' },
    all:      { t: 'Estante vazia', s: 'Adicione o primeiro livro.', b: 'Adicionar' },
  };
  const t = text[filter] || text.all;
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div className="serif" style={{ fontSize: 28, lineHeight: 1.15, color: 'var(--ink)' }}>{t.t}</div>
      <div style={{ fontSize: 14, color: 'var(--ink-3)', marginTop: 8, fontStyle: 'italic' }}>{t.s}</div>
      <button onClick={onAdd} className="btn btn-ember" style={{ marginTop: 20 }}>
        <Icon name="plus" size={16} color="var(--paper)" stroke={2} /> {t.b}
      </button>
    </div>
  );
}
